import {
  modularScale,
  media,
  Spacing,
  pxToRem,
  palette,
  Text,
} from "@ottatech/design";
import styled from "styled-components";
import { useEffect } from "react";
import { useScroll, motion, AnimatePresence } from "framer-motion";

import { Apply } from "../icons/Apply";
import { Heart } from "../icons/Heart";
import { EmptyHeart } from "../icons/EmptyHeart";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${media({ from: 769 })`
    align-items: flex-start;
  `}
`;

const ButtonText = styled(Text)`
  padding-left: ${pxToRem(8)};
  white-space: nowrap;
`;

const StyledAnimateContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  padding-left: ${modularScale()};
  position: absolute;
  left: 25px;
`;

const ActionButton = styled(motion.button)<{ isSave?: boolean }>`
  -webkit-tap-highlight-color: transparent;
  outline: none;
  display: flex;
  align-items: center;
  border-radius: ${pxToRem(100)};
  border: 1px solid ${palette.grayscale.shade400};
  box-shadow: 0px 0px 20px 0px #0000001d;
  position: relative;
  cursor: pointer;
  height: 45px;
  transition: transform 0.3s ease-in-out;
  color: ${palette.brand.black};

  letter-spacing: 0.03em;

  background-color: ${({ isSave }) =>
    isSave ? palette.brand.yellow : palette.grayscale.shade200};

  path {
    fill: ${palette.brand.black};
  }

  &:active {
    transform: scale(0.88);
  }
`;

const IconWrapper = styled.div<{ isSave?: boolean; isNext?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  width: 60px;
  position: absolute;

  svg {
    height: ${pxToRem(16)};
    width: ${pxToRem(16)};

    path {
      fill: ${({ isNext }) => (isNext ? "transparent" : palette.brand.black)};
      transition: all 0.3s ease-in-out;
    }
  }
`;

const buttonVariants = {
  circle: {
    width: 60,
    height: 60,
    padding: 0,
  },
  save: {
    width: 115,
    height: 45,
  },
  apply: {
    width: 125,
    height: 45,
  },
  experiment: {
    width: 175,
    height: 45,
  },
};

const SCROLL_TRIGGER = 20;

const BUTTON_TRANSITION = {
  default: {
    ease: "easeInOut",
    duration: 0.5,
  },
};

interface IVariantProps {
  isSave?: boolean;
  isWideScreen: boolean;
  showMiniActionButton?: boolean;
}

const getVariant = ({
  isSave,
  isWideScreen,
  showMiniActionButton,
}: IVariantProps): string => {
  if (showMiniActionButton && !isWideScreen) {
    return "circle";
  }
  return isSave ? "save" : "apply";
};

interface IActionPanelProps {
  onSave: () => void;
  onApply: () => void;
  onNext?: () => void;
  isSaved: boolean;
  isApplied: boolean;
  isWideScreen: boolean;
  showMiniActionButton?: boolean;
  setShowMiniActionButton?: (show: boolean) => void;
  showApplyButton: boolean;
  testId?: string;
}

export function ActionPanel({
  onSave,
  onApply,
  isSaved,
  isApplied,
  isWideScreen,
  showMiniActionButton,
  setShowMiniActionButton,
  showApplyButton,
  testId,
}: IActionPanelProps): React.ReactElement {
  const { scrollY } = useScroll();

  useEffect(() => {
    if (!setShowMiniActionButton || showMiniActionButton) {
      return;
    }

    return scrollY.onChange(() => {
      if (scrollY.get() > SCROLL_TRIGGER) {
        setShowMiniActionButton(true);
      }
    });
  }, [setShowMiniActionButton, scrollY, showMiniActionButton]);

  return (
    <Container data-testid={testId}>
      <Spacing>
        {showApplyButton && (
          <ActionButton
            onClick={onApply}
            key="apply-button"
            data-testid="apply-button"
            variants={buttonVariants}
            initial={false}
            animate={getVariant({
              isWideScreen,
              showMiniActionButton,
            })}
            transition={BUTTON_TRANSITION}
          >
            <IconWrapper>
              <Apply />
            </IconWrapper>
            <AnimatePresence initial={false}>
              {showMiniActionButton ? null : isApplied ? (
                <AnimateContainer containerKey="applied-button-text">
                  <ButtonText bold>Applied</ButtonText>
                </AnimateContainer>
              ) : (
                <AnimateContainer containerKey="apply-button-text">
                  <ButtonText bold>Apply</ButtonText>
                </AnimateContainer>
              )}
            </AnimatePresence>
          </ActionButton>
        )}
        <ActionButton
          onClick={onSave}
          key="save-button"
          data-testid="save-button"
          variants={buttonVariants}
          initial={false}
          animate={getVariant({
            isSave: true,
            isWideScreen,
            showMiniActionButton,
          })}
          transition={BUTTON_TRANSITION}
          isSave
        >
          <IconWrapper isSave>
            {isSaved ? <Heart /> : <EmptyHeart />}
          </IconWrapper>
          <AnimatePresence initial={false}>
            {(isWideScreen || !showMiniActionButton) && (
              <AnimateContainer containerKey="save-button-text">
                <ButtonText bold>{isSaved ? "Saved" : "Save"}</ButtonText>
              </AnimateContainer>
            )}
          </AnimatePresence>
        </ActionButton>
      </Spacing>
    </Container>
  );
}

const AnimateContainer = ({
  containerKey,
  children,
}: {
  containerKey: string;
  children: React.ReactNode | React.ReactNode[];
}): JSX.Element => (
  <StyledAnimateContainer
    key={containerKey}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, transition: { duration: 0.1, delay: 0 } }}
    transition={{ duration: 0.2, delay: 0.2 }}
  >
    {children}
  </StyledAnimateContainer>
);
